import React, { useEffect, useState } from 'react';
import type { ComponentPropsWithoutRef, ReactNode, SyntheticEvent } from 'react';

import PropTypes from 'prop-types';

import { FlagIcon } from 'apps/auth/components/common/FlagIcon/FlagIcon';
import { Icon } from 'apps/auth/components/common/Icon/Icon';
import { Text } from 'apps/auth/components/common/Text/Text';
import clsx from 'clsx';

import styles from './DiskBadge.module.css';

export type DiskBadgeVariant = 'primary' | 'secondary' | 'tertiary' | 'letterIndex' | 'selectedPassive' | 'transparent' | 'hasBorderedImg';

export type DiskBadgeSize = 'x-small' | 'small' | 'fixed-small' | 'large';

const variantClass: Record<DiskBadgeVariant, string> = {
    primary: styles.primary,
    secondary: styles.secondary,
    tertiary: styles.tertiary,
    letterIndex: styles.letterIndex,
    selectedPassive: styles.selectedPassive,
    transparent: styles.transparent,
    hasBorderedImg: styles.hasBorderedImg,
};

const sizeClass: Record<DiskBadgeSize, string> = {
    'x-small': styles.xSmall,
    small: styles.small,
    'fixed-small': styles.fixedSmall,
    large: styles.large,
};

const getIconColor = (variant: DiskBadgeVariant | undefined, isDisabled?: boolean, isInactive?: boolean) => {
    switch (variant) {
        case 'primary':
            return 'white';
        case 'secondary':
        case 'tertiary':
            return isDisabled ? 'lightGray' : isInactive ? 'secondary' : 'primary';
        case 'selectedPassive':
            return 'primary';
        case 'transparent':
            return 'primary';
        default:
            return 'primary';
    }
};

export interface DiskBadgeProps extends Omit<ComponentPropsWithoutRef<'span'>, 'children'> {
    isDisabled?: boolean;
    isInactive?: boolean;
    iconName?: string;
    flagName?: string;
    secondFlagName?: string;
    diskText?: string;
    src?: string;
    alt?: string;
    logoSymbol?: ReactNode;
    withoutRadius?: boolean;
    onError?: (event: SyntheticEvent<HTMLImageElement, Event>) => void;
    variant?: DiskBadgeVariant;
    diskSize?: DiskBadgeSize;
}

export const DiskBadge = ({
    isDisabled,
    isInactive,
    variant = 'tertiary',
    diskSize = 'small',
    logoSymbol,
    iconName,
    src,
    alt,
    flagName,
    secondFlagName,
    diskText,
    withoutRadius,
    onError,
    className,
    ...rest
}: DiskBadgeProps) => {
    const [errorImageLoading, setErrorImageLoading] = useState(false);

    useEffect(() => {
        setErrorImageLoading(false);
    }, [src]);

    return (
        <span
            className={clsx(
                styles.badge,
                variantClass[errorImageLoading ? 'tertiary' : variant],
                sizeClass[diskSize],
                isDisabled && styles.disabled,
                isInactive && styles.inactive,
                src && styles.noBorder,
                className,
            )}
            {...rest}
        >
            {iconName && !errorImageLoading && (
                <Icon name={iconName} color={getIconColor(variant, isDisabled, isInactive)} size={diskSize === 'large' ? '48px' : '24px'} />
            )}
            {/*Загрузка аватарки*/}
            {src && !errorImageLoading && (
                <span
                    className={clsx(
                        styles.img,
                        sizeClass[diskSize],
                        variant === 'hasBorderedImg' && styles.hasBorderedImg,
                        withoutRadius && styles.withoutRadius,
                    )}
                >
                    <img src={src} alt={alt} onError={(e) => (onError ? onError(e) : setErrorImageLoading(true))} />
                </span>
            )}
            {/*Текст вместо иконки или аватарки*/}
            {diskText && (
                <Text size={diskSize === 'large' ? 'header' : 'main'} color={getIconColor(variant, isDisabled, isInactive)}>
                    {diskText}
                </Text>
            )}
            {/*обработка ошибок загрузки изображения*/}
            {errorImageLoading && (
                <Text size={diskSize === 'large' ? 'header' : 'main'} color={getIconColor(variant, isDisabled, isInactive)}>
                    {logoSymbol}
                </Text>
            )}
            {flagName && (
                <span className={styles.flagWrap}>
                    <span className={styles.flag}>
                        <FlagIcon name={flagName} size={diskSize === 'large' ? 'x-small' : '16px'} />
                    </span>
                </span>
            )}
            {secondFlagName && (
                <span className={styles.secondFlagWrap}>
                    <span className={styles.flag}>
                        <FlagIcon name={secondFlagName} size={diskSize === 'large' ? 'x-small' : '16px'} />
                    </span>
                </span>
            )}
        </span>
    );
};

DiskBadge.propTypes = {
    variant: PropTypes.oneOf(['primary', 'secondary', 'tertiary', 'letterIndex', 'selectedPassive', 'transparent', 'hasBorderedImg']),
    diskSize: PropTypes.oneOf(['x-small', 'small', 'fixed-small', 'large']),
    isDisabled: PropTypes.bool,
    isInactive: PropTypes.bool,
    iconName: PropTypes.string,
    flagName: PropTypes.string,
    secondFlagName: PropTypes.string,
    diskText: PropTypes.string,
    src: PropTypes.string,
    alt: PropTypes.string,
    logoSymbol: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    withoutRadius: PropTypes.bool,
    onError: PropTypes.func,
};
